import { FC, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Rating,
  Link,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid2,
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { HeroPng } from '@carcareers/assets';
import { Intercom, Search, SearchType, AppLinks, RecruitingServicesButton } from '@carcareers/components';

const IconSpinner = lazy(() => import('@carcareers/components').then((m) => ({ default: m.IconSpinner })));

export const Home: FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Intercom />
      <Box
        sx={{
          pt: { xs: 8, md: 15 },
          pb: { xs: 4, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 4,
                }}
              >
                Finally, a hiring site for{' '}
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                    mb: 1,
                  }}
                >
                  Auto-Related
                  <Box
                    sx={{
                      backgroundColor: 'warning.light',
                      borderRadius: '10px',
                      width: '100%',
                      height: '5px',
                      position: 'absolute',
                      bottom: 2,
                      left: 0,
                      zIndex: -1,
                      animation: 'wipeIn 2s cubic-bezier(0.8, 0, 0.2, 1)',
                      '@keyframes wipeIn': {
                        '0%': {
                          width: '0%',
                        },
                        '100%': {
                          width: '100%',
                        },
                      },
                    }}
                  />
                </Box>{' '}
                Careers!
              </Typography>
              <Typography
                component="h2"
                variant="h4"
                sx={(theme) => ({
                  mb: 2,
                  fontWeight: 500,
                  color: theme.palette.grey[600],
                })}
              >
                Up to ⅓ of the jobs in the United States are auto-related. Hiring for Anybody, Everybody, Anything,
                Everything automotive related.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <Suspense fallback={<Box sx={{ width: 400, height: 400 }} />}>
                <IconSpinner size={400} iconSize={50} radius={150} rotationDuration={20} />
              </Suspense>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <WorkOutlineIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Find Your Career
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse auto-related jobs. Fast, easy, and free for job seekers.
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    '&:hover': { bgcolor: 'warning.main', color: 'warning.contrastText' },
                  }}
                  onClick={() => navigate('/auth/signup/jobseeker')}
                >
                  Get Started — Free
                </Button>
              </CardActions>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <PostAddIcon sx={{ fontSize: 40, color: '#d65969', mb: 1 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Post a Job
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reach qualified automotive candidates. $329 per ad for 30 days.
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ bgcolor: '#d65969', color: 'white', '&:hover': { bgcolor: '#b84558', color: 'white' } }}
                  onClick={() => navigate('/auth/signup/employer')}
                >
                  Post Now
                </Button>
              </CardActions>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <PeopleOutlineIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Recruiting Services
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Let our team handle the search. We find the right hire 5x faster.
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <RecruitingServicesButton variant="contained" color="primary" fullWidth />
              </CardActions>
            </Card>
          </Grid2>
        </Grid2>
      </Container>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, sm: 6, md: 10 },
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Stack
          spacing={{ xs: 2, sm: 3, md: 4 }}
          useFlexGap
          sx={{
            alignItems: 'center',
            width: '100%',
            maxWidth: { xs: '100%', sm: '90%', md: '70%' },
            mb: 16,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            useFlexGap
            sx={{
              pt: { xs: 1, sm: 2, md: 3 },
              width: '100%',
            }}
          >
            <Search searchType={SearchType.NO_AUTH_HOME} />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
            }}
          >
            By clicking &quot;Search Jobs&quot; you agree to our&nbsp;
            <Link href="terms" color="text.secondary">
              Terms & Conditions
            </Link>
            .
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 4, sm: 6, md: 8 },
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
          }}
        >
          <Stack
            spacing={{ xs: 4, sm: 6, md: 8 }}
            useFlexGap
            sx={{
              alignItems: 'center',
              width: '100%',
              maxWidth: { xs: '100%', sm: '80%', md: '50%' },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                textAlign: 'center',
              }}
            >
              Download Our Mobile App!
            </Typography>
            <AppLinks />
          </Stack>
          <Box
            sx={{
              position: 'relative',
              width: { xs: '80%', sm: '70%', md: '50%' },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '120%',
                height: '80%',
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-53%, -55%)',
                width: '80%',
                height: '50%',
                borderRadius: '50%',
                backgroundColor: 'primary.contrastText',
                zIndex: 1,
              }}
            />
            <Box
              component="img"
              src={HeroPng}
              sx={{
                height: 'auto',
                width: '100%',
                position: 'relative',
                zIndex: 2,
              }}
              alt="Hand holding cellphone with carcareers dot com's mobile application on the screen"
            />
          </Box>
        </Box>
        <Divider variant="middle" flexItem sx={{ mb: { xs: 6, sm: 8 }, mt: '-5px', zIndex: 1 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: { xs: 4, sm: 6 },
            width: '100%',
          }}
        >
          <Rating
            value={5}
            precision={1}
            readOnly
            size={isMobile ? 'medium' : 'large'}
            sx={{
              transform: {
                xs: 'scale(1)',
                sm: 'scale(1.15)',
                md: 'scale(1.25)',
              },
            }}
          />
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mt: { xs: 2, sm: 3, md: 4 },
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              textAlign: 'center',
              px: 2,
            }}
          >
            Rated the #1 hiring site for automotive jobs in the Nation!
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 5,
            position: 'relative',
            width: '100%',
            py: { xs: 6, sm: 8, md: 10 },
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 2,
              backgroundColor: 'secondary.dark',
            },
          }}
        >
          <Typography
            variant="h2"
            component="h3"
            sx={{
              color: 'common.white',
              textAlign: 'center',
              position: 'relative',
              mb: 3,
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Subscribe To Receive Updates!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'common.white',
              textAlign: 'center',
              position: 'relative',
              mb: 4,
              maxWidth: 600,
            }}
          >
            Stay updated with the latest automotive industry news, job opportunities, and career insights delivered
            straight to your inbox.
          </Typography>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: '100%',
              maxWidth: 600,
              position: 'relative',
            }}
          >
            <TextField
              fullWidth
              placeholder="Enter your email address"
              variant="outlined"
              sx={{
                backgroundColor: 'common.white',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                height: 56,
              }}
            >
              Subscribe Now
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Home;
